/**
 * AppWithClerk - App Component with Clerk Authentication
 * This component uses Clerk hooks and must be rendered inside ClerkProvider
 */

import React, { useState, useEffect, useRef } from 'react';
import { useClerk } from '@clerk/clerk-react';
import { LandingPage } from './components/LandingPage';
import { IllusionTest } from './components/IllusionTest';
import { LoadingScreen } from './components/LoadingScreen';
import { Results } from './components/Results';
import { UnifiedHandwritingFlow } from './components/UnifiedHandwritingFlow';
import { CharacterCardGeneration } from './components/CharacterCardGeneration';
import { ComingSoon } from './components/ComingSoon';
import { TermsModal } from './components/TermsModal';
import { InviteFriend } from './components/InviteFriend';
import { InviteAcceptance } from './components/InviteAcceptance';
import { ComparativeAnalysisPage } from './components/ComparativeAnalysisPage';
import { SynergyPreview } from './components/SynergyPreview';
import Dashboard from './components/Dashboard';
import { TermsOfService } from './components/TermsOfService';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { Analytics, trackPageView } from './components/Analytics';
import { useBackendUser } from './hooks/useBackendUser';
import './App.css';
import indexing from './seo/indexing.cjs';
const { isIndexable } = indexing;

// Backend API configuration
const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_URL) {
        return process.env.REACT_APP_API_URL;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const apiParam = urlParams.get('api');
    if (apiParam) {
        return apiParam;
    }
    return '/api';
};

const API_BASE_URL = getApiBaseUrl();

function App() {
    // Use Clerk-connected hook
    const {
        backendUser: currentUser,
        isLoading: isUserLoading,
        isSignedIn,
        clerkUser
    } = useBackendUser();
    const { openSignUp, openSignIn } = useClerk();

    const [currentState, setCurrentState] = useState(() => {
        const path = window.location.pathname;
        if (path === '/terms') return 'terms';
        if (path === '/privacy') return 'privacy';
        return 'landing';
    });
    const [hasCompletedCard, setHasCompletedCard] = useState(false);
    useEffect(() => {
        const update = () => {
            const indexable = isIndexable(currentState, window.location.pathname, window.location.search);
            const robots = document.querySelector('meta[name="robots"]');
            if (robots) robots.setAttribute('content', indexable ? 'index, follow' : 'noindex, follow');
        };
        update();
        window.addEventListener('popstate', update);
        return () => window.removeEventListener('popstate', update);
    }, [currentState]);
    // destResolved: true once we know where to send the signed-in user.
    // Prevents flash of landing page during the auth waterfall.
    const [destResolved, setDestResolved] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0 });
    const [analysisResult, setAnalysisResult] = useState(null);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [error, setError] = useState(null);
    const [inviteCode, setInviteCode] = useState(null);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [compareConnection, setCompareConnection] = useState(null);
    const [compareInitialTheme, setCompareInitialTheme] = useState('work');
    const [compareAutoStart, setCompareAutoStart] = useState(false);

    // Pre-warmed session refs — fire session/create in background while user reads landing page
    const prewarmedSession = useRef(null);
    const prewarmedPromise = useRef(null);

    // Reset destResolved whenever the user signs in so the shell covers the new auth waterfall
    useEffect(() => {
        if (isSignedIn) setDestResolved(false);
    }, [isSignedIn]);

    // Pre-warm: fire session/create while user reads the landing page so click is instant
    useEffect(() => {
        if (currentState !== 'landing') return;
        prewarmedSession.current = null;
        const referrerId = sessionStorage.getItem('referrerId') || undefined;
        prewarmedPromise.current = fetch(`${API_BASE_URL}/session/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ referrer_id: referrerId })
        })
            .then(res => res.json())
            .then(data => { if (data.success) prewarmedSession.current = data; })
            .catch(() => {}); // silent — handleStart falls back gracefully
    }, [currentState]);

    // Check for invite code or share link on app load
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const inviteParam = urlParams.get('invite');
        const shareParam = urlParams.get('share');
        const refParam = urlParams.get('ref');

        if (refParam) {
            localStorage.setItem('referrerId', refParam);
        }

        if (inviteParam) {
            // Always show the invite page first — let the user decide to link or take the test.
            // Clerk may not be loaded yet so we NEVER check currentUser here.
            localStorage.setItem('pendingInviteCode', inviteParam);
            localStorage.removeItem('mindquest_app_state'); // Clear stale sessions
            setInviteCode(inviteParam);
            setCurrentState('invite-acceptance');
        } else if (shareParam) {
            fetchSharedCard(shareParam);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSharedCard = async (cardId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/share/card/${cardId}`);
            const data = await response.json();

            if (data.success && data.card) {
                try {
                    // Look up the active invite for the card owner
                    const inviteRes = await fetch(`${API_BASE_URL}/invites/active/${data.card.owner_id}`);
                    const inviteData = await inviteRes.json();
                    
                    if (inviteData.success && inviteData.invite_code) {
                        window.history.replaceState(null, '', `/?invite=${inviteData.invite_code}`);
                        setInviteCode(inviteData.invite_code);
                        setCurrentState('invite-acceptance');
                        return;
                    }
                } catch (e) {
                    console.error('Failed to fetch owner invite from shared card:', e);
                }
                
                // Fallback to error if invite fails
                setError('Shared link expired or invite not found.');
            } else {
                setError('Shared card not found or expired.');
            }
        } catch (err) {
            console.error('Failed to load shared card:', err);
            setError('Failed to load shared card.');
        }
    };

    // Persist active state to localStorage to survive Auth redirects and browser restarts.
    // Only save states that are safe to restore (have all required data in localStorage).
    const SAFE_TO_SAVE = new Set(['dashboard', 'welcome-personalized', 'handwriting-upload', 'character-card-generation', 'results', 'coming-soon', 'synergy-preview']);
    useEffect(() => {
        if (SAFE_TO_SAVE.has(currentState)) {
            const stateToSave = {
                currentState,
                sessionId,
                analysisResult,
                progress
            };
            localStorage.setItem('mindquest_app_state', JSON.stringify(stateToSave));
        }
    }, [currentState, sessionId, analysisResult, progress]);

    // Restore state on load if returning from Auth or redirect logged-in users
    useEffect(() => {
        if (!isSignedIn) {
            // User just signed out or is not signed in
            // If we are on a protected page, send to landing
            const protectedStates = ['welcome-personalized', 'results', 'handwriting-intro', 'handwriting-upload'];
            if (protectedStates.includes(currentState)) {
                setCurrentState('landing');
                localStorage.removeItem('mindquest_app_state');
            }
            return;
        }

        // Must wait for backend user record to sync before routing an authenticated user
        if (isUserLoading) return;

        const savedState = localStorage.getItem('mindquest_app_state');
        const urlParams = new URLSearchParams(window.location.search);
        const shareParam = urlParams.get('share');
        const inviteParam = urlParams.get('invite');

        // If user is on the invite page, InviteAcceptance handles auto-linking
        // via its own useEffect — don't interfere here
        if (currentState === 'invite-acceptance') {
            return;
        }

        if (savedState && !shareParam && !inviteParam) {
            try {
                const parsedState = JSON.parse(savedState);
                // Only restore if we have a valid state and are currently at 'landing' (default)
                if (parsedState && currentState === 'landing') {
                    if (parsedState.sessionId) setSessionId(parsedState.sessionId);
                    if (parsedState.analysisResult) setAnalysisResult(parsedState.analysisResult);
                    if (parsedState.progress) setProgress(parsedState.progress);

                    const dashboardIntercepts = ['landing', 'welcome-personalized', 'handwriting-upload', 'character-card-generation', 'dashboard'];

                    // If user just signed in and was on results/signup, move them forward
                    if (parsedState.currentState === 'results') {
                        if (parsedState.analysisResult && currentUser?.id) {
                            const saveAnalysis = async () => {
                                try {
                                    await fetch(`${API_BASE_URL}/user/save-analysis`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                            user_id: currentUser.id,
                                            analysis_data: parsedState.analysisResult,
                                            session_id: parsedState.sessionId
                                        })
                                    });

                                    const pendingConnectionId = sessionStorage.getItem('unlinkedConnectionId');
                                    if (pendingConnectionId) {
                                        await fetch(`${API_BASE_URL}/invites/link`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({
                                                connection_id: pendingConnectionId,
                                                user_id: currentUser.id
                                            })
                                        });
                                        sessionStorage.removeItem('unlinkedConnectionId');
                                        sessionStorage.setItem('dashboard_active_tab', 'sync');
                                        sessionStorage.setItem('show_link_toast', 'true');
                                    }
                                } catch (e) {
                                    console.error('Failed to migrate guest data:', e);
                                } finally {
                                    if (currentUser?.id) {
                                        checkUserCard(currentUser.id);
                                    } else {
                                        setCurrentState('dashboard');
                                        setDestResolved(true);
                                    }
                                }
                            };
                            saveAnalysis();
                        } else if (currentUser?.id) {
                            checkUserCard(currentUser.id);
                        } else {
                            setCurrentState('dashboard');
                            setDestResolved(true);
                        }
                    } else if (dashboardIntercepts.includes(parsedState.currentState)) {
                        if (currentUser?.id) {
                            checkUserCard(currentUser.id);
                        } else {
                            setCurrentState('welcome-personalized');
                            setDestResolved(true);
                        }
                    } else {
                        // All other saved states (testing, results, loading, comparative-analysis, etc.)
                        // require in-memory data that isn't in localStorage — restoring them directly
                        // causes a blank screen. Route to the safest landing point instead.
                        if (currentUser?.id) {
                            checkUserCard(currentUser.id);
                        } else {
                            setCurrentState('welcome-personalized');
                            setDestResolved(true);
                        }
                    }
                }
            } catch (e) {
                console.error('Failed to restore state', e);
                localStorage.removeItem('mindquest_app_state');
                setDestResolved(true);
            }
        } else {
            // Logged in user with no specific saved state restoration needed
            // OR restoration just happened.
            const startingStates = ['landing', 'welcome-personalized'];
            if (startingStates.includes(currentState) && currentUser?.id && !hasCompletedCard) {
                checkUserCard(currentUser.id);
            } else {
                // Already on a valid destination — no navigation needed
                setDestResolved(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSignedIn, currentUser, isUserLoading]);

    const checkUserCard = async (userId) => {
        try {
            const clerkId = clerkUser?.id;
            const cardUrl = clerkId
                ? `${API_BASE_URL}/user/card?userId=${userId}&clerk_id=${clerkId}`
                : `${API_BASE_URL}/user/card?userId=${userId}`;
            const response = await fetch(cardUrl);
            const data = await response.json();

            if (data.success && data.card) {
                // Restore card state
                setAnalysisResult({
                    ...data.card.card_data, // The stored JSON
                    card_id: data.card.card_id,
                    isSharedView: false // User is owner
                });
                setHasCompletedCard(true);
                // Reset to profile tab on fresh login unless an in-session action set it (e.g. invite → sync)
                const pendingTab = sessionStorage.getItem('dashboard_active_tab');
                if (!pendingTab || pendingTab === 'account') {
                    sessionStorage.setItem('dashboard_active_tab', 'profile');
                }
                setCurrentState('dashboard');
                localStorage.removeItem('mindquest_app_state'); // Done with guest flow
                setDestResolved(true);
            } else {
                checkUserAnalysis(userId);
            }
        } catch (err) {
            console.error('Failed to check user card:', err);
            checkUserAnalysis(userId);
        }
    };

    const checkUserAnalysis = async (userId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/user/analysis?userId=${userId}`);
            const data = await response.json();
            if (data.success && data.analysis) {
                setAnalysisResult(data.analysis);
            }
        } catch (err) {
            console.error('Failed to check user analysis:', err);
        } finally {
            setHasCompletedCard(false);
            const forceDashboard = sessionStorage.getItem('forceDashboard');
            if (forceDashboard === 'true') {
                sessionStorage.removeItem('forceDashboard');
                setCurrentState('dashboard');
            } else {
                setCurrentState('welcome-personalized');
            }
            setDestResolved(true);
        }
    };

    // Track page views and scroll to top when state changes
    useEffect(() => {
        // Always scroll to top on navigation — covers both window and inner scroll containers
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        // Reset any inner scrollable containers (Dashboard .tab-content, Results, etc.)
        document.querySelectorAll('.tab-content, .results-page, .landing-page, .welcome-personalized').forEach(el => {
            el.scrollTop = 0;
        });

        const pageNames = {
            'landing': 'You Don\'t Know You',
            'testing': 'Illusion Test',
            'loading': 'Processing Results',
            'results': 'Test Results',
            'coming-soon': 'Coming Soon',
            'welcome-personalized': 'Welcome',
            'handwriting-intro': 'Why Handwriting?',
            'handwriting-upload': 'Handwriting Analysis',
            'character-card-generation': 'Character Card Generation',
            'coming-soon-complete': 'Registration Complete',
            'invite-friend': 'Invite Friend',
            'invite-acceptance': 'Accept Invite',
            'dashboard': 'Dashboard',
            'synergy-preview': 'Synergy Match',
            'comparative-analysis': 'Comparative Analysis'
        };

        const pageName = pageNames[currentState] || 'You Don\'t Know You';
        trackPageView(`/${currentState}`, pageName);
        document.title = pageName === 'You Don\'t Know You' ? 'AI Life Coach — What your handwriting reveals | YDKY' : `${pageName} | YDKY`;
    }, [currentState]);

    const handleStart = async () => {
        try {
            setError(null);
            let data;

            if (prewarmedSession.current?.success) {
                // Best case: session already ready, zero wait
                data = prewarmedSession.current;
                prewarmedSession.current = null;
            } else if (prewarmedPromise.current) {
                // Pre-warm still in flight — await it instead of firing a second request
                await prewarmedPromise.current;
                if (prewarmedSession.current?.success) {
                    data = prewarmedSession.current;
                    prewarmedSession.current = null;
                }
            }

            if (!data) {
                // Fallback: pre-warm failed or wasn't started (e.g. invite flow)
                const referrerId = sessionStorage.getItem('referrerId');
                const response = await fetch(`${API_BASE_URL}/session/create`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ referrer_id: referrerId })
                });
                data = await response.json();
            }

            if (!data.success) throw new Error(data.error || 'Failed to create session');

            setSessionId(data.session.id);

            if (data.question && data.progress) {
                setCurrentQuestion(data.question);
                setProgress(data.progress);
            } else {
                await loadCurrentQuestion(data.session.id);
            }

            setCurrentState('testing');
        } catch (err) {
            console.error('Failed to start session:', err);
            setError('Failed to start test. Please try again.');
        }
    };

    const loadCurrentQuestion = async (sessionIdParam) => {
        // Skip question loading if we are in the middle of an invite redirect.
        // Only check inviteCode React state — pendingInviteCode stays in sessionStorage
        // through the entire test so the final submit can call acceptInvite, but it must
        // NOT block mid-test question fetching (inviteCode is null once the test starts).
        if (inviteCode) return;
        
        try {
            const response = await fetch(`${API_BASE_URL}/session/${sessionIdParam}/question`);
            const data = await response.json();
            if (!data.success) {
                throw new Error(data.error || 'Failed to load question');
            }
            setCurrentQuestion(data.question);
            setProgress(data.progress);
        } catch (err) {
            console.error('Failed to load question:', err);
            setError('Failed to load question. Please try again.');
        }
    };

    const handleAnswer = async (optionId) => {
        try {
            setError(null);
            const response = await fetch(`${API_BASE_URL}/session/${sessionId}/answer`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ option_id: optionId })
            });
            const data = await response.json();
            if (!data.success) {
                throw new Error(data.error || 'Failed to submit answer');
            }
            if (data.session_completed) {
                setAnalysisResult(data.analysis);
                setCurrentState('loading');
            } else {
                await loadCurrentQuestion(sessionId);
            }
        } catch (err) {
            console.error('Failed to submit answer:', err);
            setError('Failed to submit answer. Please try again.');
        }
    };

    const handleLoadingComplete = async () => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        try {
            if (analysisResult) {
                // Persist state before moving to results
                localStorage.setItem('mindquest_app_state', JSON.stringify({
                    currentState: 'results',
                    sessionId,
                    analysisResult,
                    progress
                }));

                const pendingInviteCode = sessionStorage.getItem('pendingInviteCode');
                if (pendingInviteCode) {
                    const res = await acceptInvite(pendingInviteCode, analysisResult);
                    if (res && res.success) {
                        if (res.connection_id) {
                            sessionStorage.setItem('unlinkedConnectionId', res.connection_id);
                        }
                        if (res.inviter_name) {
                            sessionStorage.setItem('connected_user_name', res.inviter_name);
                        }
                    }
                    sessionStorage.removeItem('pendingInviteCode');
                }

                // Save to DB immediately if user is already signed in
                if (currentUser?.id) {
                    fetch(`${API_BASE_URL}/user/save-analysis`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            user_id: currentUser.id,
                            analysis_data: analysisResult,
                            session_id: sessionId
                        })
                    }).catch(e => console.error('Failed to save analysis:', e));
                }

                setCurrentState('results');
            } else {
                await loadCurrentQuestion(sessionId);
                setCurrentState('testing');
            }
        } catch (err) {
            console.error('Failed to proceed:', err);
            setError('Failed to proceed. Please try again.');
        }
        setIsTransitioning(false);
    };

    const acceptInvite = async (inviteCodeParam, testResults) => {
        try {
            const res = await fetch(`${API_BASE_URL}/invites/accept`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    invite_code: inviteCodeParam,
                    user_id: currentUser?.id || null,
                    test_results: testResults
                })
            });
            return await res.json();
        } catch (err) {
            console.error('Failed to accept invite:', err);
            return null;
        }
    };

    const handleViewFullAnalysis = () => setCurrentState('coming-soon');

    const handleGoToSignup = () => {
        // Save current state before potential redirect/modal
        localStorage.setItem('mindquest_app_state', JSON.stringify({
            currentState: 'results',
            sessionId,
            analysisResult,
            progress
        }));

        if (isSignedIn) {
            setCurrentState('welcome-personalized');
        } else {
            // Show Terms & Conditions modal before opening Clerk sign up
            setShowTermsModal(true);
        }
    };

    const handleTermsAgree = () => {
        setShowTermsModal(false);
        openSignUp();
    };

    const handleTermsClose = () => {
        setShowTermsModal(false);
    };

    const handleSignInClick = () => {
        if (openSignIn) {
            openSignIn({ mode: 'modal' });
        }
    };
    const handleGoHome = () => {
        if (isSignedIn && currentUser?.id) {
            checkUserCard(currentUser.id);
        } else {
            setCurrentState('landing');
        }
    };

    const handleHandwritingUploadComplete = (data) => {
        if (data) setAnalysisResult(data);
        setCurrentState('character-card-generation');
    };
    const handleCharacterCardComplete = () => setCurrentState('coming-soon-complete');
    const handleInviteFriend = () => setCurrentState('invite-friend');
    const handleBackToResults = () => setCurrentState('results');

    const handleInviteStartTest = () => {
        window.history.replaceState({}, document.title, window.location.pathname);
        setInviteCode(null);
        handleStart();
    };

    // Called when a logged-in user clicks "Link Profile" on the invite page.
    // Silently links their account then routes to their dashboard.
    const handleLinkProfile = (inviterName = null) => {
        const code = inviteCode || sessionStorage.getItem('pendingInviteCode');
        if (!code || !currentUser?.id) return;
        
        const performLink = async () => {
            let fetchedConnectionId = null;
            let fetchedInviterName = null;
            try {
                const res = await fetch(`${API_BASE_URL}/invites/accept`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        invite_code: code,
                        user_id: currentUser.id,
                        test_results: null
                    })
                });
                const data = await res.json();
                // Capture id + name whether accept succeeded or was already-connected
                if (data.connection_id) fetchedConnectionId = data.connection_id;
                if (data.inviter_name) fetchedInviterName = data.inviter_name;
            } catch (err) {
                console.error('Failed to link profile:', err);
            }

            // Clean up URL/storage — do NOT touch React state yet so InviteAcceptance
            // doesn't re-validate with inviteCode=null and flash an error screen
            sessionStorage.removeItem('pendingInviteCode');
            window.history.replaceState({}, document.title, window.location.pathname);

            // Always fetch fresh connection list — the DB row exists even when accept
            // returned an error partway through (e.g. idempotency / partial failure)
            try {
                const connsRes = await fetch(`${API_BASE_URL}/invites/user/${currentUser.id}`);
                const connsData = await connsRes.json();
                if (connsData.success && connsData.invites) {
                    const accepted = connsData.invites.filter(
                        c => c.status === 'accepted' || c.status === 'completed'
                    );
                    // Prefer the exact connection we just created; fall back to most recent
                    const matchedConn = fetchedConnectionId
                        ? accepted.find(c => String(c.id) === String(fetchedConnectionId)) || accepted[0]
                        : accepted[0];

                    if (matchedConn) {
                        // No toast here — SynergyPreview already confirms the connection visually
                        if (fetchedInviterName || inviterName) {
                            sessionStorage.setItem('connected_user_name', fetchedInviterName || inviterName);
                        }
                        // Fetch the user's own card so profile1 is populated for auto-analysis
                        try {
                            const clerkId = clerkUser?.id;
                            const cardUrl = clerkId
                                ? `${API_BASE_URL}/user/card?userId=${currentUser.id}&clerk_id=${clerkId}`
                                : `${API_BASE_URL}/user/card?userId=${currentUser.id}`;
                            const cardRes = await fetch(cardUrl);
                            const cardData = await cardRes.json();
                            if (cardData.success && cardData.card) {
                                setAnalysisResult({
                                    ...cardData.card.card_data,
                                    card_id: cardData.card.card_id,
                                    isSharedView: false
                                });
                            }
                        } catch (e) {
                            console.error('Failed to fetch user card for synergy:', e);
                        }
                        // Set all React state together so InviteAcceptance unmounts cleanly
                        setInviteCode(null);
                        setCompareConnection(matchedConn);
                        setCompareInitialTheme('work');
                        setCompareAutoStart(false);
                        setDestResolved(true);
                        setCurrentState('synergy-preview');
                        return;
                    }
                }
            } catch (e) {
                console.error('Failed to fetch connection for auto-compare:', e);
            }

            // Fallback: couldn't find any connection — land on sync tab
            setInviteCode(null);
            checkUserCard(currentUser.id);
            sessionStorage.setItem('dashboard_active_tab', 'sync');
        };
        performLink();
    };

    const handleViewCards = () => setCurrentState('character-card-generation');
    const handleResumeTest = () => setCurrentState('welcome-personalized');

    const handleRestart = () => {
        setCurrentState('landing');
        setSessionId(null);
        setCurrentQuestion(null);
        setProgress({ current: 0, total: 0, percentage: 0 });
        setAnalysisResult(null);
        setIsTransitioning(false);
        setError(null);
        setInviteCode(null);
        window.history.replaceState({}, document.title, window.location.pathname);
        sessionStorage.removeItem('pendingInviteCode');
    };

    // Retest from dashboard: go straight to handwriting upload, keep existing analysis
    const handleRetestFromDashboard = () => {
        setIsTransitioning(false);
        setError(null);
        setCurrentState('welcome-personalized');
    };

    // Error state
    if (error) {
        return (
            <div className="App">
                <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', minHeight: '100vh',
                    background: 'linear-gradient(180deg, #2E003E 0%, #0F1020 100%)',
                    color: 'white', padding: '2rem', textAlign: 'center'
                }}>
                    <h2>Oops! Something went wrong</h2>
                    <p>{error}</p>
                    <button onClick={handleRestart} style={{
                        background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                        border: 'none', borderRadius: '25px', color: 'white',
                        padding: '1rem 2rem', fontSize: '1.1rem', fontWeight: '600',
                        cursor: 'pointer', marginTop: '1rem'
                    }}>Try Again</button>
                </div>
            </div>
        );
    }

    // Show branded shell only when user is confirmed signed-in but destination not yet resolved.
    // Signed-out users skip this entirely — landing renders immediately.
    const SHELL_BYPASS = ['invite-acceptance', 'terms', 'privacy'];
    if (isSignedIn && !destResolved && !SHELL_BYPASS.includes(currentState)) {
        return (
            <div className="App" style={{
                minHeight: '100vh',
                background: 'linear-gradient(180deg, #2E003E 0%, #0F1020 100%)'
            }} />
        );
    }

    return (
        <div className="App">
            <Analytics />

            <TermsModal
                isOpen={showTermsModal}
                onAgree={handleTermsAgree}
                onClose={handleTermsClose}
            />

            {currentState === 'landing' && (
                <LandingPage onStart={handleStart} user={currentUser} onSignInClick={handleSignInClick} />
            )}

            {currentState === 'testing' && currentQuestion && (
                <IllusionTest
                    illusion={currentQuestion}
                    onAnswer={handleAnswer}
                    questionNumber={progress.current}
                    totalQuestions={progress.total}
                    user={currentUser}
                    onSignInClick={handleSignInClick}
                />
            )}

            {currentState === 'loading' && (
                <LoadingScreen onComplete={handleLoadingComplete} user={currentUser} onSignInClick={handleSignInClick} />
            )}

            {currentState === 'results' && analysisResult && (
                <Results
                    analysisResult={analysisResult}
                    onRestart={handleRestart}
                    onViewFullAnalysis={handleViewFullAnalysis}
                    onInviteFriend={handleInviteFriend}
                    onGoToSignup={handleGoToSignup}
                    user={currentUser}
                    onSignInClick={handleSignInClick}
                />
            )}

            {currentState === 'invite-friend' && analysisResult && (
                <InviteFriend
                    analysisResult={analysisResult}
                    user={currentUser}
                    onSignInClick={handleSignInClick}
                    onBack={handleBackToResults}
                />
            )}

            {currentState === 'invite-acceptance' && inviteCode && (
                <InviteAcceptance
                    inviteCode={inviteCode}
                    user={currentUser}
                    onSignInClick={handleSignInClick}
                    onStartTest={handleInviteStartTest}
                    onLinkProfile={handleLinkProfile}
                />
            )}

            {currentState === 'coming-soon' && (
                <ComingSoon onRestart={handleRestart} onSignup={handleGoToSignup} user={currentUser} onSignInClick={handleSignInClick} />
            )}

            {currentState === 'welcome-personalized' && (
                <UnifiedHandwritingFlow
                    user={currentUser}
                    analysisResult={analysisResult}
                    onSignInClick={handleSignInClick}
                    onUploadComplete={handleHandwritingUploadComplete}
                    onGoHome={handleGoHome}
                />
            )}

            {currentState === 'character-card-generation' && (
                <CharacterCardGeneration
                    user={currentUser}
                    analysisResult={analysisResult}
                    onSignInClick={handleSignInClick}
                    onBack={() => setCurrentState('handwriting-upload')}
                    onComplete={handleCharacterCardComplete}
                    onGoHome={handleGoHome}
                    onGoToConnections={() => {
                        sessionStorage.setItem('dashboard_active_tab', 'sync');
                        setHasCompletedCard(true);
                        setCurrentState('dashboard');
                    }}
                />
            )}

            {currentState === 'dashboard' && (
                <Dashboard 
                    user={currentUser} 
                    analysisResult={analysisResult} 
                    hasCompletedCard={hasCompletedCard}
                    onViewCards={handleViewCards}
                    onResumeTest={handleResumeTest}
                    onRestart={handleRetestFromDashboard} 
                    onNavigateToCompare={(conn, theme) => {
                        setCompareConnection(conn);
                        setCompareInitialTheme(theme || 'work');
                        setCompareAutoStart(false);
                        setDestResolved(true);
                        setCurrentState('synergy-preview');
                    }}
                />
            )}

            {currentState === 'synergy-preview' && compareConnection && (
                <SynergyPreview
                    user={currentUser}
                    analysisResult={analysisResult}
                    connection={compareConnection}
                    onReveal={() => setCurrentState('comparative-analysis')}
                    onBack={() => {
                        sessionStorage.setItem('dashboard_active_tab', 'sync');
                        setCurrentState('dashboard');
                    }}
                    onSignInClick={handleSignInClick}
                    onGoHome={handleGoHome}
                />
            )}

            {currentState === 'comparative-analysis' && compareConnection && (
                <ComparativeAnalysisPage
                    user={currentUser}
                    analysisResult={analysisResult}
                    connection={compareConnection}
                    initialThemeId={compareInitialTheme}
                    autoStart={compareAutoStart}
                    onBack={() => {
                        sessionStorage.setItem('dashboard_active_tab', 'sync');
                        setCurrentState('dashboard');
                    }}
                    onSignInClick={handleSignInClick}
                />
            )}

            {currentState === 'coming-soon-complete' && (
                <ComingSoon onRestart={handleRestart} onSignup={handleGoToSignup} isComplete={true} user={currentUser} onSignInClick={handleSignInClick} />
            )}

            {currentState === 'terms' && (
                <TermsOfService />
            )}

            {currentState === 'privacy' && (
                <PrivacyPolicy />
            )}
        </div>
    );
}

export default App;
